import React, { useEffect, useMemo, useState } from 'react'
import Decimal from 'decimal.js/decimal'
import clsx from 'clsx'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import { ButtonSquare } from '@/modules/base/components/buttons'
import ShoppingCartItem from '@/modules/moduleSubscription/components/list/shopping-cart/shopping-cart-item/shopping-cart-item'
import ShoppingCartIcon from '@assets/images/icons/shopping-cart.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import ThemeInterface from '@/theme-interface'
import { dateConstants } from '@/modules/base/util/constants'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import { Module, Subscription } from '@/modules/moduleSubscription/types/module'
import { removeItemFromCart } from '@/modules/moduleSubscription/components/_helpers/removeItemFromCart'
import { addItemToCart } from '@/modules/moduleSubscription/components/_helpers/addItemToCart'
import getVat1Germany from '@/modules/moduleSubscription/components/_helpers/getVat1Germany'
import getSums from '@/modules/moduleSubscription/components/_helpers/getSums'
import { PeriodType, Type } from '@/redux/ducks/subscription/types/details'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { useAppSelector } from '@/redux/store'
import { StepButtonTitles } from '@/modules/base/components/modal/upgrade-modal-v2/hooks/useUpgradeSteps'
import { STEPS } from '@/redux/ducks/upgrade/wizard'
import { checkIsAdminRole } from '@/modules'
import { handleConfirmCheckoutForGtm } from '@/modules/moduleSubscription/components/_helpers/gtmHandlers'
import navigateToCurrentPage from '@/utils/navigateToCurrentPage'

interface ShoppingCartProps {
  showCheckoutButton?: boolean;
  items?: ShoppingCartItems;
  onChange?: (type?: Type, module?: Module, quantity?: number) => void;
  onDelete?: (type: Type, name: string) => void;
  onContinue?: (unknown: unknown) => void;
  readOnly?: boolean;
  onSelectedPeriodChange?: (selectedPeriod: PeriodType) => void;
  selectedPeriod?: string;
  className?: string
  enableItemsFromStorage?: boolean;
  enablePeriodSelection?: boolean;
  hideTitle?: boolean;
  isBillingFormValid?: boolean;
  showContinueButton?: boolean;
  scrollbarHeight?: string;
}

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    background: 'linear-gradient(225.85deg, #4277FC 5.01%, #1F2E53 89.98%)',
    boxShadow: '0px 4px 12px 0px #4277FC80',
    padding: 5,
    height: '100%',
  },
  emptyViewDescription: {
    fontSize: theme.fontSizes.medium,
    textAlign: 'center',
  },
  emptyViewContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    height: '100%',
    justifyContent: 'center',
  },
  legalMessages: {
    fontSize: theme.fontSizes.tiny,
    color: theme.textLight,
    marginTop: 10,
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
  },
  sumItem: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div:first-of-type & span': {
      whiteSpace: 'nowrap',
    },
  },
  sumItemPrice: {
    fontWeight: theme.fontWeights.medium,
    whiteSpace: 'nowrap',
  },
  titleWrapper: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  scrollbar: {
    '& > .ps__rail-y': {
      zIndex: 1,
    },
    '&.scrollLock': {
      '& > :first-child': {
        height: '100%',
        overflow: 'hidden',
      },
    },
  },
  shoppingCartTitle: {
    color: theme.black,
    fontSize: theme.fontSizes.medium,
  },
  shoppingCartContainer: {
    height: '100%',
    gap: 10,
    display: 'flex',
    flexDirection: 'column',
    background: theme.white,
    borderRadius: 4,
    padding: 20,
    flex: 1,
    [theme.devices.mobile]: {
      height: 'calc(100%  - 40px)',
    },
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.borderColor,
    margin: '15px 0',
  },
  checkoutButton: {
    maxHeight: 45,
    minHeight: 45,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    background: theme.backgroundColor,
    borderRadius: 4,

    '& p': {
      margin: 0,
      marginBottom: 5,
      fontWeight: theme.fontWeights.medium,
    },
  },
  shoppingCartItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  checkoutFooterLine: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 5,
    textAlign: 'center',
    fontSize: theme.fontSizes.tiny,
    marginBottom: 0,
    marginTop: 15,
  },
  shoppingCartHeader: {
    padding: 10,
    color: theme.white,
  },
}))

const ShoppingCart: React.FC<ShoppingCartProps> = (props) => {
  const { activeStep, activeStepIndex, stepsInOrder } = useAppSelector(state => state.upgrade.wizard)
  const openUpgradeModal = useUpgradeModal()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    showCheckoutButton = true,
    items = {},
    onChange = () => {},
    readOnly = false,
    selectedPeriod = dateConstants.YEAR,
    className,
    enableItemsFromStorage,
    hideTitle = false,
    scrollbarHeight = '50vh',
    onDelete,
    onContinue,
    isBillingFormValid = false,
    showContinueButton = false,
  } = props

  const vat1Germany = useMemo(() => getVat1Germany(), [])

  const userInfo = useSelector(state => state.user.info)
  const [period, setPeriod] = useState<string>(dateConstants.YEAR)
  const [shoppingCartItems, setShoppingCartItems] = useState<ShoppingCartItems>(items)

  useEffect(() => {
    setPeriod(selectedPeriod)
  }, [selectedPeriod])

  useEffect(() => {
    if (enableItemsFromStorage) {
      const shoppingCart = WebStorageService.getItem(WebStorageKey.SHOPPING_CART)
      if (shoppingCart?.items) setShoppingCartItems(shoppingCart?.items)
    }
  }, [])

  useEffect(() => {
    setShoppingCartItems(items)
  }, [items])

  const onDeleteItemFromCart = (type: string, itemName: string) => {
    removeItemFromCart(type, itemName, shoppingCartItems, enableItemsFromStorage)
    if (enableItemsFromStorage) {
      const shoppingCart = WebStorageService.getItem(WebStorageKey.SHOPPING_CART)
      if (shoppingCart) setShoppingCartItems(shoppingCart.items)
    } else {
      setShoppingCartItems(items)
    }
    onChange?.()
    onDelete?.(type as Type, itemName)
  }

  const onAddItemToCart = (type: string, item: Module | Subscription, count: number, replace = false) => {
    addItemToCart(type, item, count, replace, shoppingCartItems, enableItemsFromStorage)
    if (enableItemsFromStorage) {
      const shoppingCart = WebStorageService.getItem(WebStorageKey.SHOPPING_CART)
      if (shoppingCart) setShoppingCartItems(shoppingCart.items)
    } else {
      setShoppingCartItems(items)
    }
    onChange?.(type as Type, item, count)
  }

  const onConfirmCheckout = () => {
    handleConfirmCheckoutForGtm(shoppingCartItems)
    openUpgradeModal({
      showAllModules: true,
      enableItemsFromStorage,
      preSelectedPeriod: period,
      onUpgraded: () => {
        onChange?.()
        navigateToCurrentPage(navigate, location)
      },
      onClose: () => {
        onChange?.()
        navigateToCurrentPage(navigate, location)
      },
    })
  }

  const cartIsEmpty = Object.keys(shoppingCartItems).reduce((disabled, type) => {
    if (disabled) {
      disabled = !Object.keys(shoppingCartItems[type]).length
    }
    return disabled
  }, true)
  const classes = useStyles({ cartIsEmpty })

  const sums = useMemo(() => {
    return getSums(Object.keys(items).length ? items : shoppingCartItems, vat1Germany, null, period)
  }, [shoppingCartItems, items, period])

  const legalMessage = period === dateConstants.YEAR
    ? lang.moduleSubscription.list.shoppingCart.legalMessage
    : lang.moduleSubscription.list.shoppingCart.legalMessageMonth

  const emptyView = cartIsEmpty
    ? (
      <div className={classes.emptyViewContainer}>
        <SVGIcon svg={ShoppingCartIcon} height='15px' width='15px' />
        <div className={classes.emptyViewDescription}>
          {lang.moduleSubscription.list.shoppingCart.empty}
        </div>
      </div>
    )
    : null

  const continueButtonLabel = useMemo(() => {
    return StepButtonTitles[stepsInOrder[activeStepIndex + 1]] || lang.upgrade.wizard.footer.button.continue
  }, [activeStep])

  const continueButtonDisabled = useMemo(() => {
    return activeStep === STEPS.BILLING && !isBillingFormValid
  }, [activeStep, isBillingFormValid])

  const disableCheckout = cartIsEmpty || !checkIsAdminRole(userInfo) || continueButtonDisabled

  const shoppingCart = useMemo(() => {
    if (cartIsEmpty) return null

    return (
      <>
        <PerfectScrollbar
          options={{ suppressScrollX: true, scrollYMarginOffset: 10 }}
          className={clsx(classes.scrollbar, 'forcePerfectScrollbarRight')}
          style={{ maxHeight: scrollbarHeight }}>
          <div className={classes.shoppingCartItems}>
            {Object.keys(shoppingCartItems).map((type, index) => {
              return (
                <React.Fragment key={`${type}-${index}`}>
                  <ShoppingCartItem
                    item={{ ...shoppingCartItems[type] }}
                    type={type}
                    onDelete={onDeleteItemFromCart}
                    onChange={onAddItemToCart}
                    readOnly={readOnly}
                    selectedPeriod={period}
                  />
                </React.Fragment>
              )
            })}
          </div>
        </PerfectScrollbar>
        <div>

        </div>
        <div className={classes.footer}>
          <div className={classes.sumItem}>
            <div>Zwischensumme</div>
            <div className={classes.sumItemPrice}>{formatCurrency(sums.totalNet)}</div>
          </div>
          <div className={classes.sumItem}>
            <div>USt. {vat1Germany?.name || '19%'}</div>
            <div className={classes.sumItemPrice}>{formatCurrency(new Decimal(sums.totalGross).minus(sums.totalNet).toDP(2).toNumber())}</div>
          </div>
          <div className={classes.divider} />
          <div className={classes.sumItem}>
            <div>Gesamt</div>
            <div className={classes.sumItemPrice}>{formatCurrency(sums.totalGross)}</div>
          </div>
          <div className={classes.legalMessages}>{legalMessage}</div>
        </div>
        {!cartIsEmpty && showContinueButton && <div>
          <ButtonSquare customClass={classes.checkoutButton} onClick={onContinue} label={continueButtonLabel} disabled={disableCheckout} />
          <p className={classes.checkoutFooterLine}><VerifiedUserIcon sx={{ fontSize: 18 }} />{lang.moduleSubscription.list.shoppingCart.checkoutFooterLine}</p>
        </div>}
        {!cartIsEmpty && showCheckoutButton && <div>
          <ButtonSquare customClass={classes.checkoutButton} onClick={onConfirmCheckout} label={lang.moduleSubscription.list.shoppingCart.checkout} disabled={disableCheckout} />
          <p className={classes.checkoutFooterLine}><VerifiedUserIcon sx={{ fontSize: 18 }} />{lang.moduleSubscription.list.shoppingCart.checkoutFooterLine}</p>
        </div>}
      </>
    )
  }, [shoppingCartItems, items, onDeleteItemFromCart, onAddItemToCart, period])

  return (
    <div className={classes.container}>
      <div className={classes.shoppingCartHeader}>
        {lang.moduleSubscription.list.shoppingCart.title}
      </div>
      <div className={clsx(classes.shoppingCartContainer, className)}>
        {!hideTitle && <div className={classes.titleWrapper}>
          <div className={classes.shoppingCartTitle}>{lang.moduleSubscription.list.shoppingCart.title}</div>
          <SVGIcon svg={ShoppingCartIcon} height='15px' width='15px' />
        </div>}
        <div className={classes.header}>
          <div>
            <p>{lang.moduleSubscription.list.shoppingCart.periodTitleLabel}</p>
            <span>{lang.moduleSubscription.list.shoppingCart.periodInMonths(12)}</span>
          </div>
          <div>
            <p>{lang.moduleSubscription.list.shoppingCart.billingPeriodLabel}</p>
            <span>{lang.moduleSubscription.list.shoppingCart.periods[selectedPeriod]}</span>
          </div>
        </div>
        {shoppingCart || emptyView}
      </div>
    </div>
  )
}

export default ShoppingCart
